/** Legacy server placeholders are accepted while APIs return ordinary values. */
export const isMaskedValue = (value) => Boolean(value && typeof value === 'object'
  && value.$masked === true && typeof value.display === 'string'
  && typeof value.ref === 'string' && typeof value.fingerprint === 'string');

export const maskSensitiveText = (value) => {
  if (isMaskedValue(value)) return value.display;
  if (value == null || value === '') return value;
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  const characters = Array.from(text);
  const visible = characters.length > 2 ? 2 : 0;
  return characters.slice(0, visible).join('') + '*'.repeat(characters.length - visible);
};

/** Read-only projection. Never use the result as form state or a request value. */
export const maskedValuesForDisplay = (value, sensitive = false) => {
  if (isMaskedValue(value)) return value.display;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object') {
        const displayed = maskedValuesForDisplay(parsed, sensitive);
        return JSON.stringify(displayed) === JSON.stringify(parsed) ? value : JSON.stringify(displayed);
      }
    } catch { /* Ordinary scalar text. */ }
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    if (value.type === 'template' || value.type === 'tab') {
      return { ...value, children: (value.children || []).map((child) => maskedValuesForDisplay(child, sensitive)) };
    }
    if ('default_value' in value) {
      return { ...value, default_value: sensitive || value.is_sensitive
        ? maskSensitiveText(value.default_value) : maskedValuesForDisplay(value.default_value) };
    }
  }
  if (sensitive) return maskSensitiveText(value);
  if (Array.isArray(value)) return value.map((item) => maskedValuesForDisplay(item));
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value)
    .map(([key, child]) => [key, maskedValuesForDisplay(child)]));
  return value;
};

/** Device documents can predate the sensitivity flags on the current definition. */
export const withTemplateSensitivity = (document, sensitive = false, definition = document) => {
  const policies = new Map();
  const visit = (node, path = []) => {
    (node?.children || []).forEach((child) => {
      const next = [...path, child.key];
      if (child.type === 'tab') visit(child, next);
      else policies.set(JSON.stringify(next), Boolean(child.is_sensitive));
    });
  };
  visit(definition);
  const hasSensitive = Array.from(policies.values()).some(Boolean);
  const project = (node, path = []) => {
    if (!node || typeof node !== 'object') return node;
    if (Array.isArray(node.children)) return { ...node, children: node.children.map((child) => project(child, [...path, child.key])) };
    const key = JSON.stringify(path);
    return sensitive || node.is_sensitive || policies.get(key) || (hasSensitive && !policies.has(key))
      ? { ...node, is_sensitive: true } : node;
  };
  return project(document);
};

export const hasSensitiveValues = (value) => Boolean(value && typeof value === 'object'
  && (value.is_sensitive || isMaskedValue(value) || Object.values(value).some(hasSensitiveValues)));

/** Historical log rows may not include a current definition; hide parameter values locally. */
export const maskParameterLogPayload = (value) => {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object') return JSON.stringify(maskParameterLogPayload(parsed));
    } catch { /* Plain log text. */ }
    return value;
  }
  if (Array.isArray(value)) return value.map(maskParameterLogPayload);
  if (!value || typeof value !== 'object') return value;
  const result = Object.fromEntries(Object.entries(value).map(([key, child]) => [key, maskParameterLogPayload(child)]));
  const valueKeys = ['parameter_required', 'parameter_current', 'required_value', 'current_value', 'transition_value', 'default_value'];
  if ('parameter_name' in value || 'path' in value || (value.name && value.name !== 'status')) valueKeys.push('value');
  valueKeys.forEach((key) => { if (key in value) result[key] = maskSensitiveText(value[key]); });
  return result;
};

export const maskParameterAuditChanges = (changes, contentType) => {
  if (contentType !== 'commonlib.parameter' || !changes || !('default_value' in changes)) return changes;
  const value = changes.default_value;
  return { ...changes, default_value: value && typeof value === 'object' && 'old' in value && 'new' in value
    ? { old: maskSensitiveText(value.old), new: maskSensitiveText(value.new) } : maskSensitiveText(value) };
};

export const parameterLogValueForDisplay = (value, parameter) => {
  if (parameter.is_sensitive) return maskSensitiveText(value);
  let document = value;
  if (typeof value === 'string') {
    try { document = JSON.parse(value); } catch { return value; }
  }
  return maskedValuesForDisplay(document?.type === 'template'
    ? withTemplateSensitivity(document, false, parameter.default_value) : value);
};
